import { Accordion, Alert, Box, Divider, Group, Select, Stack, Text, Tooltip } from '@mantine/core';
import { Info, Lock } from 'react-feather';
import { SESSION_DURATION_OPTIONS, MODALITY_LABELS, ONE_ON_ONE_DURATION_OPTIONS } from '../../../../config/cohortConfig';
import {
  ONE_ON_ONE_TYPES,
  ONE_ON_ONE_TYPE_LABELS,
  DEFAULT_ONE_ON_ONE_TYPE,
} from '../../../../types';
import type { ModalityConfigRow, ModalitySessionConfig } from './editCohortTypes';
import { CheckinSettingsFields, type CheckinSettingsForm } from '../CheckinSettingsFields';

interface SessionConfigSectionProps {
  modalityConfigs: ModalityConfigRow[];
  canEditSessionConfig: boolean;
  showDurationWarning: boolean;
  updateModalityConfig: (modalityId: string, field: keyof ModalityConfigRow, value: number | null) => void;
  updateModalitySessionConfigs: (modalityId: string, sessionConfigs: ModalitySessionConfig[]) => void;
  // MED-1350: intro and midpoint check-ins
  checkins: CheckinSettingsForm;
  onCheckinsChange: (next: CheckinSettingsForm) => void;
  programWeeks: number | null;
  checkinsError?: string | null;
  /** MED-1350: the cohort start date, for the check-in week date ranges. */
  startDate?: Date | string | null;
}

export function SessionConfigSection({
  modalityConfigs,
  canEditSessionConfig,
  showDurationWarning,
  updateModalityConfig,
  updateModalitySessionConfigs,
  checkins,
  onCheckinsChange,
  programWeeks,
  checkinsError,
  startDate,
}: SessionConfigSectionProps) {
  if (modalityConfigs.length === 0) return null;

  // MED-1350: the check-ins sit at the end of the 1:1 box, or at the end of
  // the Group Coaching box when the cohort has no 1:1 coaching.
  const checkinHostType = modalityConfigs.some((m) => m.modality_type === 'one_on_one')
    ? 'one_on_one'
    : 'group_coaching';

  const updateSessionConfig = (
    modalityId: string,
    configs: ModalitySessionConfig[],
    index: number,
    updates: Partial<ModalitySessionConfig>,
  ) => {
    const newConfigs = [...configs];
    newConfigs[index] = { ...newConfigs[index], ...updates };
    updateModalitySessionConfigs(modalityId, newConfigs);
  };

  return (
    <Accordion.Item value="session-config">
      <Accordion.Control>
        <Text fw={600}>Session Configuration</Text>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="md">
          {!canEditSessionConfig && (
            <Alert icon={<Lock size={18} />} color="gray" variant="light">
              <Text size="sm">
                Session configuration can only be edited when cohort is in Draft or In Prep status.
              </Text>
            </Alert>
          )}

          {showDurationWarning && (
            <Alert icon={<Info size={18} />} color="orange" variant="light">
              <Text size="sm" fw={500}>
                Updating duration will affect existing session end times
              </Text>
              <Text size="sm" c="dimmed">
                Sessions have already been scheduled. Changing the duration will update the calculated end times for all
                sessions.
              </Text>
            </Alert>
          )}

          {modalityConfigs.map((config) => {
            const isOneOnOne = config.modality_type === 'one_on_one';
            const durationOptions = isOneOnOne ? ONE_ON_ONE_DURATION_OPTIONS : SESSION_DURATION_OPTIONS;

            return (
              <Box
                key={config.id}
                p="md"
                style={{
                  backgroundColor: 'var(--mantine-color-gray-0)',
                  borderRadius: '8px',
                  border: '1px solid var(--mantine-color-gray-3)',
                }}
              >
                <Text size="sm" fw={600} mb="sm">
                  {MODALITY_LABELS[config.modality_type as keyof typeof MODALITY_LABELS] || config.modality_type}
                </Text>
                <Group gap="xl">
                  <Box>
                    <Text size="xs" c="dimmed" mb={4}>
                      Sessions
                    </Text>
                    <Text size="sm" fw={500}>
                      {config.session_count || '\u2014'}
                    </Text>
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed" mb={4}>
                      Frequency
                    </Text>
                    <Text size="sm" fw={500} style={{ textTransform: 'capitalize' }}>
                      {config.session_frequency || '\u2014'}
                    </Text>
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Text size="xs" c="dimmed" mb={4}>
                      Session Duration
                    </Text>
                    <Select
                      value={config.session_duration_minutes ? String(config.session_duration_minutes) : null}
                      onChange={(value) =>
                        updateModalityConfig(config.id, 'session_duration_minutes', value ? parseInt(value, 10) : null)
                      }
                      data={durationOptions.map((opt) => ({
                        value: String(opt.value),
                        label: opt.label,
                      }))}
                      placeholder="Select duration"
                      disabled={!canEditSessionConfig}
                      style={{ width: 120 }}
                      size="sm"
                    />
                  </Box>
                </Group>

                {/* Per-session configuration for 1:1 coaching (AC14-16) */}
                {isOneOnOne && config.sessionConfigs.length > 0 && (
                  <Box mt="md">
                    <Divider mb="sm" />
                    <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb="sm" style={{ letterSpacing: '0.3px' }}>
                      Per-Session Configuration
                    </Text>
                    <Stack gap="xs">
                      {config.sessionConfigs.map((sc, index) => (
                        <Group
                          key={sc.sessionNumber}
                          gap="md"
                          p="xs"
                          style={{
                            backgroundColor: 'white',
                            borderRadius: '6px',
                            border: '1px solid var(--mantine-color-gray-2)',
                          }}
                        >
                          <Text size="sm" fw={500} style={{ width: 90 }}>
                            Session {sc.sessionNumber}
                          </Text>
                          <Select
                            value={String(sc.durationMinutes)}
                            onChange={(value) =>
                              updateSessionConfig(config.id, config.sessionConfigs, index, {
                                durationMinutes: value ? parseInt(value, 10) : (config.session_duration_minutes ?? 60),
                              })
                            }
                            data={ONE_ON_ONE_DURATION_OPTIONS.map((opt) => ({
                              value: String(opt.value),
                              label: opt.label,
                            }))}
                            size="xs"
                            style={{ width: 100 }}
                            disabled={!canEditSessionConfig}
                          />
                          <Tooltip
                            label="Session type cannot be changed after scheduling"
                            disabled={!sc.isScheduled}
                            withArrow
                          >
                            <Select
                              aria-label={`Session ${sc.sessionNumber} type`}
                              value={sc.oneOnOneType}
                              onChange={(value) =>
                                updateSessionConfig(config.id, config.sessionConfigs, index, {
                                  oneOnOneType:
                                    (value as typeof ONE_ON_ONE_TYPES[number]) ??
                                    DEFAULT_ONE_ON_ONE_TYPE,
                                })
                              }
                              data={ONE_ON_ONE_TYPES.map((t) => ({
                                value: t,
                                label: ONE_ON_ONE_TYPE_LABELS[t],
                              }))}
                              size="xs"
                              style={{ flex: 1, minWidth: 200 }}
                              disabled={!canEditSessionConfig || sc.isScheduled}
                            />
                          </Tooltip>
                        </Group>
                      ))}
                    </Stack>
                  </Box>
                )}

                {config.modality_type === checkinHostType && (
                  <CheckinSettingsFields
                    value={checkins}
                    onChange={onCheckinsChange}
                    programWeeks={programWeeks}
                    startDate={startDate}
                    error={checkinsError}
                    borderColor="var(--mantine-color-gray-2)"
                  />
                )}
              </Box>
            );
          })}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}
